// src/services/markAllRead.js — "Mark all read", with the read-receipt gate.
//
// Mirrors HomeScreen's Mark All Read, including the part that is easy to miss:
// clearing MY badge and telling the SENDER are two different things, and only
// the second one is a privacy decision.
//
//   • every chat gets its badge cleared — unreadCounts[me] = 0 and
//     manuallyUnread[me] = false. That is my own counter; nobody derives a
//     receipt from it.
//   • the read WATERMARK (readUpTo[me]) — the thing a sender's green tick is
//     derived from — is published ONLY for chats where my read receipts are on,
//     resolved per chat: the chat's own `readReceiptsBy[me]` override first,
//     then my global `privacySettings.readReceipts`.
//
// 🟥 FAILS CLOSED. If my profile hasn't loaded we cannot know the global
//    setting, so nothing is published — the app logs and suppresses in exactly
//    this case too. Reporting a read you shouldn't have is not recoverable.

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { markChatRead } from './receipts';
import { tsMs } from './chats';

/**
 * @returns {Promise<{cleared:number, reported:number, suppressed:number}>}
 */
export async function markAllRead(chats, myUid) {
  let profileLoaded = false;
  let globalOn = false;
  try {
    const snap = await getDoc(doc(db, 'users', myUid));
    const p = snap.data()?.privacySettings;
    profileLoaded = p !== undefined;
    globalOn = profileLoaded && p?.readReceipts !== false;
  } catch { /* stays false → fails closed */ }

  let cleared = 0; let reported = 0; let suppressed = 0;

  for (const chat of chats || []) {
    if (!chat?.id) continue;
    // nothing to mark: no badge and nothing newer than what I've read
    const unread = Number(chat.unreadCounts?.[myUid]) || 0;
    const manual = chat.manuallyUnread?.[myUid] === true;
    if (!unread && !manual) continue;
    // the last thing in here is mine → there is no one to report to
    if (chat.lastMessageSenderId && chat.lastMessageSenderId === myUid && !manual) continue;

    // 🟥 the chat doc's OWN timestamp, never Date.now(): the server compares
    //    watermarks across participants, and a now-floor from one device would
    //    falsely satisfy that comparison for everyone.
    const newest = tsMs(chat.timestamp || chat.lastMessageTime);
    if (!newest) continue;

    // per-chat override wins over the global, same resolution as everywhere else
    const perChat = chat.readReceiptsBy?.[myUid];
    const rrOn = profileLoaded && (perChat != null ? perChat !== false : globalOn);

    // eslint-disable-next-line no-await-in-loop
    await markChatRead(chat.id, myUid, newest, { publishRead: rrOn }).catch(() => {});
    cleared += 1;
    if (rrOn) reported += 1; else suppressed += 1;
  }

  return { cleared, reported, suppressed };
}
